import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import ChevronRightLightSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/ChevronRightLightSVG';
import DynamicDropdownWithDropdownHeader from '@jobcase/react_pattern_library/components/DynamicDropdown/DynamicDropdownWithDropdownHeader';
import Shimmer from '@jobcase/react_pattern_library/components/Shimmer';
import SVGIcon from '@jobcase/react_pattern_library/components/SVGIcon/icons-v2/ChevronRightLightSVG';
import Typography from '@jobcase/react_pattern_library/components/Typography/TypographyRebrand';
import styles from '@jobcase/react_pattern_library/styles/rebrand';

import DropDownHeaderComponent from '../DropDownHeaderComponent/DropDownHeaderComponentRebrand';
import DynamicDropdownListObject from '../DynamicDropdownListObject';

import {
  getCurrentEmployer, getCurrentSubEmployer,
  getHasAllEmployersGrants,
  getHasMultipleEmployers,
  getJobcaseEmployerAdminLevel,
} from '../../../redux/nodes/features/employers/selectors';
import { getIsLoading as getIsGrantsLoading } from '../../../redux/nodes/entities/grants/selectors';
import { getIsLoading as getIsHiringEventsLoading } from '../../../redux/nodes/entities/hiringEvents/selectors';
import { getIsNavCollapsed } from '../../../redux/nodes/application/selectors';
import { getIsUpdating, getOrganizationById, getOrganizationsAdminDetails, isAdminOrganizationLoading } from '../../../redux/nodes/features/settings/selectors';
import { getOrganization, getOrganizationAdminDetails } from '../../../redux/nodes/features/settings/actions';
import employerActions from '../../../redux/nodes/entities/employers/actions';

import { ORGANIZATION_STATUS_COLOR_MAP, ORGANIZATION_STATUS_READABLE_MAP } from '../../../utils/constants/settings';
import { isEmpty, toSentenceCase } from '../../../utils/utils';
import { useAppContext, useSelector } from '../../../utils/hooks';
import { getSubOrgById } from '../../../redux/nodes/entities/subOrganizations/selectors';
import config from '../../../config';

const EMPTY_FILTERED_EMPLOYERS_ID = 0;

const SecondaryTypography = styled(Typography)`
  align-items: center;
  display: flex;

  &&& {
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
    font-weight: 600;
  }
`;

const StyledLogo = styled.img`
  border: 1px solid ${styles.color.dividerGrayColor};
  border-radius: ${styles.spacing.borderRadius}px;
  margin-right: ${styles.spacing.tiny}px;
  width: 24px;
`;

const StyledInitial = styled.div`
  background: #44A8F4;
  border-radius: ${styles.spacing.borderRadius}px;
  border: 1px solid ${styles.color.dividerGrayColor};
  color: ${styles.color.jobcaseWhiteColor};
  font-size: ${styles.font.fontSize.body};
  font-weight: 600;
  margin-right: ${styles.spacing.tiny}px;
  text-align: center;

  @media(min-width: ${styles.breakpoint.md}) {
    min-width: 24px;
    max-width: 24px;
  }
`;

const SecondaryHeaderContainer = styled.span`
  cursor: ${({ isDisabled }) => (isDisabled ? 'default' : 'pointer')};
  line-height: ${styles.font.lineHeight.body2};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 148px;
`;

const StyledSVGIcon = styled(SVGIcon)`
`;

const DynamicAccountsDropdown = styled(DynamicDropdownWithDropdownHeader)`
  border-bottom: 1px solid ${styles.color.dividerGrayColor};
  border-top: 1px solid ${styles.color.dividerGrayColor};
  color: ${styles.color.jobcaseBlackColor};
  display: none;
  height: unset;
  padding: ${styles.spacing.tiny + 2}px ${styles.spacing.tiny}px;
  width: auto;

  @media(min-width: ${styles.breakpoint.md}) {
    display: flex;

    [class*="click-outside__ClickOutsideWrapper"] {
      width: 100%;
    }
    [class*="DynamicDropdownHeaderSecondary__DropdownTitle"] {
      min-height: 24px;
      min-width: 24px;
    }
  }

  &:hover {
    background-color: ${styles.color.commentBoxGrayColor};
  }

  .dropdown-container {
    background-color: ${styles.color.jobcaseWhiteColor};
    border-radius: ${styles.spacing.cardBorderRadius}px;
    left: ${({ isSideBarCollapsed }) => (isSideBarCollapsed ? '65' : '248')}px;
    overflow: hidden;
    top: 0;
    width: 324px;

    [class*="SearchWrapper"] {
      padding: ${styles.spacing.tiny}px;

      [class*="InputRebrand__FormFieldWrapper-sc"] {
        padding: 0;
      }
    }

    .dropdown-heading {
      font-size: ${styles.font.fontSize.body2};
      margin-bottom: ${styles.spacing.extraTiny}px;
    }

    .dropdown-input {
      input {
        outline: none;
      }

      [class*="InputRebrand__IconWrapper"] {
        top: 13px;
      }

      svg {
        height: 24px;
        margin: unset;
        width: 24px;
      }
    }

    [class*="DropDownHeaderComponentRebrand__ResetIconWrapper"] {
      right: 16px;
    }

    .dropdown-list {
      max-height: 360px;
    }

    .dropdown-list-item {
      cursor: pointer;
      display: block;
      padding: ${styles.spacing.tiny + 3}px ${styles.spacing.tiny}px;
      position: relative;

      &:hover {
        background: ${styles.color.commentBoxGrayColor};
      }

      .left-rail {
        background: ${styles.color.primaryColor};
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 4px;
      }

      ${({ isLoading }) => (!isLoading && css`
        &.empty-state {
          cursor: none;
          padding: ${styles.spacing.tiny * 4}px ${styles.spacing.tiny}px;
          pointer-events: none;
          text-align: center;

          [class^="Typography__Component-sc"] {
            color: ${styles.color.bodyTextColor};
          }
        }
      `)}
    }

    ${({ isLoading }) => (isLoading && css`
      pointer-events: none;

      &:hover {
        background-color: ${styles.color.jobcaseWhiteColor};
      }
    `)}
  }
`;

const ListItemTypography = styled(Typography)`
  cursor: pointer;
  line-height: ${styles.font.lineHeight.body2};

  && {
    font-weight: 600;
  }
`;

const OrgStatus = styled.div`
  background-color: ${({ backgroundColor }) => (backgroundColor || styles.color.commentBoxGrayColor)};
  border-radius: ${styles.spacing.borderRadius}px;
  color: ${styles.color.jobcaseBlackColor};
  font-family: ${styles.font.fontFamily};
  font-size: ${styles.font.fontSize.body2};
  font-weight: 400;
  line-height: ${styles.font.lineHeight.body2};
  margin-bottom: ${styles.spacing.extraExtraTiny}px;
  padding: ${styles.spacing.extraExtraTiny}px ${styles.spacing.extraTiny}px;
  width: fit-content;
`;

const InternalOrgNameTypography = styled(Typography)`
  color: ${styles.color.textGrayColor};
  display: block;
  margin-top: ${styles.spacing.extraExtraTiny}px;
`;

const ShimmerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${styles.spacing.extraExtraTiny}px;
`;

const EMPTY_STATE = {
  name: 'No results have been found for this search',
  id: EMPTY_FILTERED_EMPLOYERS_ID,
  selected: false,
  hovered: false,
  className: 'empty-state',
};

const DesktopEmployerSwitcher = ({ employers, onChange, isLabelHidden, setMaskDisplayed }) => {
  const { isMobile } = useAppContext();
  const dispatch = useDispatch();
  const fetchSearchResultsRef = useRef();

  const [searchQuery, setSearchQuery] = useState();
  const [areEmployersLoading, setAreEmployersLoading] = useState(false);

  const currentEmployerId = useSelector(getCurrentEmployer);
  const currentSubEmployerId = useSelector(getCurrentSubEmployer);

  const hasMultipleEmployers = useSelector(getHasMultipleEmployers);
  const isGrantsLoading = useSelector(getIsGrantsLoading);
  const isHiringEventsLoading = useSelector(getIsHiringEventsLoading);
  const isOrgNameUpdating = useSelector(getIsUpdating);
  const isSideBarCollapsed = useSelector(getIsNavCollapsed);
  const hasAllEmployersAccess = useSelector(getHasAllEmployersGrants);
  const jobcaseEmployerAdminLevel = useSelector(getJobcaseEmployerAdminLevel);
  const orgAdminIsLoading = useSelector(isAdminOrganizationLoading);
  const organizationsAdminDetails = useSelector(getOrganizationsAdminDetails);

  const currentEmployer = useSelector(getOrganizationById(currentEmployerId));
  const currentSubEmployer = useSelector(getSubOrgById(currentSubEmployerId));

  const { environment } = config;

  const employersToShow = employers[0]?.id === currentEmployerId ? [employers[0]] : [employers[0], currentEmployer];
  const [filteredEmployers, setFilteredEmployers] = useState(jobcaseEmployerAdminLevel ? employersToShow : employers);
  const areOrgDetailsLoading = orgAdminIsLoading || areEmployersLoading;
  const showMultipleEmployers = jobcaseEmployerAdminLevel || hasMultipleEmployers || hasAllEmployersAccess;
  const isSwitcherDisabled = !showMultipleEmployers || isHiringEventsLoading || isGrantsLoading;

  const isSubOrgSelected = !isEmpty(currentSubEmployer);
  const logoImageUrl = isSubOrgSelected
    ? currentSubEmployer?.logo_image?.image_url
    : currentEmployer?.logo_image?.image_url;

  const displayName = isSubOrgSelected
    ? `Sub Org: ${currentSubEmployer?.name}`
    : currentEmployer?.name;

  const resetQuery = () => {
    setSearchQuery('');
    setFilteredEmployers(jobcaseEmployerAdminLevel ? employersToShow : employers);
  };

  const onSearchHandler = async ({ target: { value } }) => {
    setSearchQuery(value);
    if (fetchSearchResultsRef.current) {
      fetchSearchResultsRef.current.cancel();
    }
    fetchSearchResultsRef.current = debounce(async () => {
      const query = value.toLowerCase().trim();
      if (query.length >= 3) {
        setAreEmployersLoading(true);

        const queryParams = {
          name: query,
          sort_field: 'status',
          desc_sort: false,
        };

        // TODO: Remove override for production once sub-org feature is ready to be fully released.
        if (environment !== 'production') {
          queryParams.include_sub_org = true;
        }

        const result = await dispatch(employerActions.getEmployersByName(queryParams));
        if (result?.data?.length < 1) {
          setFilteredEmployers([EMPTY_STATE]);
          setAreEmployersLoading(false);
          return;
        }
        const newFilteredEmployers = result?.data;
        setFilteredEmployers(newFilteredEmployers);
        if (jobcaseEmployerAdminLevel || hasAllEmployersAccess) {
          Promise.all(
            newFilteredEmployers
              .filter(employer => !organizationsAdminDetails?.[employer.id] && employer.id !== EMPTY_FILTERED_EMPLOYERS_ID)
              .map(employer => dispatch(getOrganizationAdminDetails({ organizationId: employer.id }, true))),
          );
        }
      } else {
        setFilteredEmployers([]);
      }
      setAreEmployersLoading(false);
    }, 500);
    fetchSearchResultsRef.current();
  };

  useEffect(() => {
    if (!isOrgNameUpdating) {
      setFilteredEmployers(jobcaseEmployerAdminLevel ? employersToShow : employers);
    }
  }, [isOrgNameUpdating]);

  useEffect(() => {
    if (jobcaseEmployerAdminLevel) {
      Promise.all(
        filteredEmployers
          .filter(employer => !organizationsAdminDetails?.[employer.id] && employer.id !== EMPTY_FILTERED_EMPLOYERS_ID)
          .map(employer => dispatch(getOrganizationAdminDetails({ organizationId: employer.id }, true))),
      );
    }
  }, [filteredEmployers]);

  useEffect(() => {
    dispatch(getOrganization({ organizationId: currentEmployerId }));
  }, [currentEmployerId]);

  // Enhance the result to include all matching sub-organizations belonging to the same organization.
  const expandedFilteredEmployers = useMemo(() => filteredEmployers.flatMap((employer) => {
    if (employer?.sub_orgs?.length && searchQuery?.length >= 3) {
      const expandedEmployers = [];
      if (employer?.name?.toLowerCase().includes(searchQuery?.toLowerCase())) {
        expandedEmployers.push({ ...employer });
      }

      employer.sub_orgs.forEach((subEmployer) => {
        expandedEmployers.push(
          {
            ...employer,
            subEmployer,
          },
        );
      });

      return expandedEmployers;
    }

    // Look for selected
    if (employer?.sub_orgs?.length && currentSubEmployerId) {
      const subEmployer = employer.sub_orgs.find(s => s?.id === currentSubEmployerId);

      return [{
        ...employer,
        subEmployer,
      }];
    }

    if (currentSubEmployer?.organization_id === employer?.id) {
      return [{
        ...employer,
        subEmployer: currentSubEmployer,
      }];
    }

    return [employer];
  }), [filteredEmployers, searchQuery, currentSubEmployerId]);

  if (isMobile && employers.length < 1) return null;

  return (
    <DynamicAccountsDropdown
      isLabelHidden={isLabelHidden}
      isSideBarCollapsed={isSideBarCollapsed}
      DropDownHeaderComponent={(
        <DropDownHeaderComponent
          value={searchQuery}
          onChange={onSearchHandler}
          resetQuery={resetQuery}
        />
      )}
      ListObjectComponent={<DynamicDropdownListObject />}
      classes={{
        dropDown: 'dropdown-container',
        dropDownList: 'dropdown-list',
        dropDownListItem: 'dropdown-list-item',
      }}
      customIcon={(
        <StyledSVGIcon
          svgChild={ChevronRightLightSVG}
          size={24}
          color={styles.color.textGrayColor}
          isLabelHidden={isLabelHidden}
        />
      )}
      isLoading={areOrgDetailsLoading}
      isDisabled={isSwitcherDisabled}
      options={expandedFilteredEmployers.filter(employer => !isEmpty(employer)).map((employer, index) => {
        const isSubOrg = !!(employer?.subEmployer?.id);

        const employerId = employer?.id;
        const subEmployerId = employer?.subEmployer?.id;
        const key = `${employerId}-${subEmployerId}`;

        const name = isSubOrg ? employer?.subEmployer?.name : employer?.name;
        const status = employer?.status;
        const internalOrgName = organizationsAdminDetails?.[employerId]?.internal_organization_name;

        return {
          ...employer,
          employer_id: employer?.id,
          sub_employer_id: employer?.subEmployer?.id,
          id: index,
          title: areOrgDetailsLoading ? (
            <ShimmerContainer>
              <Shimmer height="28px" width="60px" />
              <Shimmer height="24px" width="190px" />
              <Shimmer height="20px" width="140px" />
            </ShimmerContainer>
          ) : (
            <>
              {(status && (jobcaseEmployerAdminLevel || hasAllEmployersAccess)) && (
                <OrgStatus backgroundColor={ORGANIZATION_STATUS_COLOR_MAP[status]}>
                  {ORGANIZATION_STATUS_READABLE_MAP[status] || toSentenceCase(status)}
                </OrgStatus>
              )}
              <ListItemTypography key={key}>
                {isSubOrg ? 'Sub Org: ' : ''}
                {name}
              </ListItemTypography>
              {(internalOrgName && (jobcaseEmployerAdminLevel || hasAllEmployersAccess)) && (
                <InternalOrgNameTypography variant={Typography.VARIANTS.BODY2}>
                  {internalOrgName}
                </InternalOrgNameTypography>
              )}
            </>
          ),
          selectedTitle: (
            <SecondaryTypography
              key={key}
              isDisabled={isSwitcherDisabled}
            >
              {currentEmployer?.logo_image?.image_url ? (
                <StyledLogo src={currentEmployer?.logo_image?.image_url} />
              ) : (
                <StyledInitial>{name?.trim()?.split('')[0]}</StyledInitial>
              )}
              <SecondaryHeaderContainer
                isDisabled={isSwitcherDisabled}
                isLabelHidden={isLabelHidden}
                data-fullstory-unmask
              >
                {isSubOrg && 'Sub Org: '}
                {name}
              </SecondaryHeaderContainer>
            </SecondaryTypography>
          ),
          selected: employerId === currentEmployerId && subEmployerId === currentSubEmployerId,
          hovered: false,
        };
      })}
      defaultPlaceholder={(
        <SecondaryTypography isDisabled={isSwitcherDisabled}>
          {logoImageUrl ? (
            <StyledLogo src={logoImageUrl} />
          ) : (
            <StyledInitial>{displayName?.trim()?.split('')[0]}</StyledInitial>
          )}
          {isLabelHidden && (
            <SecondaryHeaderContainer
              isDisabled={isSwitcherDisabled}
              isLabelHidden={isLabelHidden}
            >
              {displayName}
            </SecondaryHeaderContainer>
          )}
        </SecondaryTypography>
      )}
      backgroundColor={styles.color.jobcaseWhiteColor}
      onChange={onChange}
      onOpen={(val) => {
        resetQuery();
        setMaskDisplayed(val);
      }}
      onClose={() => {
        resetQuery();
        setMaskDisplayed(false);
      }}
    />
  );
};

DesktopEmployerSwitcher.propTypes = {
  employers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })),
  onChange: PropTypes.func,
  isLabelHidden: PropTypes.bool,
  setMaskDisplayed: PropTypes.func,
};

DesktopEmployerSwitcher.defaultProps = {
  employers: [],
  onChange: () => {},
  isLabelHidden: false,
  setMaskDisplayed: () => {},
};

export default DesktopEmployerSwitcher;
